import React, { useEffect, useState, useCallback } from "react";
import { useUI } from "../../context/UIContext";
import { useAuth } from "../../context/AuthContext";
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
} from "../../api";
import { fmtVnTime } from "../../utils/format";

const ROLES = [
  { v: 1, label: "Admin · toàn quyền" },
  { v: 2, label: "Operator · pm2 + PID app" },
  { v: 3, label: "Guest · chỉ xem" },
];
const ROLE_NAME = { 1: "Admin", 2: "Operator", 3: "Guest" };

export default function UserManagementModal() {
  const { toast } = useUI();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // form thêm mới
  const [nu, setNu] = useState({ username: "", displayName: "", password: "", role: 2 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setUsers(await getUsers());
    } catch {
      toast("Không tải được danh sách tài khoản", "err");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!nu.username || !nu.password) return toast("Nhập username + mật khẩu", "err");
    if (nu.password.length < 6) return toast("Mật khẩu tối thiểu 6 ký tự", "err");
    setBusy(true);
    const { ok, data } = await createUser(nu);
    setBusy(false);
    if (!ok) return toast(data.message || "Lỗi tạo tài khoản", "err");
    toast("Đã tạo tài khoản", "ok");
    setNu({ username: "", displayName: "", password: "", role: 2 });
    load();
  };

  const changeRole = async (u, role) => {
    const { ok, data } = await updateUser(u.UserId, { role: Number(role) });
    if (!ok) return toast(data.message || "Lỗi", "err");
    toast("Đã đổi quyền", "ok");
    load();
  };

  const toggleLock = async (u) => {
    const { ok, data } = await updateUser(u.UserId, { isLocked: !u.IsLocked });
    if (!ok) return toast(data.message || "Lỗi", "err");
    toast(u.IsLocked ? "Đã mở khoá" : "Đã khoá", "ok");
    load();
  };

  const resetPw = async (u) => {
    const pw = window.prompt(`Mật khẩu mới cho '${u.UserName}' (≥6 ký tự):`);
    if (pw == null) return;
    if (pw.length < 6) return toast("Mật khẩu tối thiểu 6 ký tự", "err");
    const { ok, data } = await updateUser(u.UserId, { password: pw });
    if (!ok) return toast(data.message || "Lỗi", "err");
    toast("Đã đổi mật khẩu", "ok");
  };

  const remove = async (u) => {
    if (!window.confirm(`Xoá tài khoản '${u.UserName}'?`)) return;
    const { ok, data } = await deleteUser(u.UserId);
    if (!ok) return toast(data.message || "Lỗi", "err");
    toast("Đã xoá tài khoản", "ok");
    load();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
      {/* Add New User Panel */}
      <div>
        <div style={{ fontSize: "13px", fontWeight: 700, color: "var(--text)", marginBottom: "10px" }}>Thêm tài khoản mới</div>
        <div style={{
          padding: "16px",
          background: "var(--surface2)",
          border: "1px solid var(--border)",
          borderRadius: "12px",
        }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
            gap: "12px",
            alignItems: "flex-end",
          }}>
            <div>
              <div className="field-lbl">Username</div>
              <input
                className="field-inp"
                value={nu.username}
                onChange={(e) => setNu({ ...nu, username: e.target.value })}
                style={{ marginBottom: 0 }}
              />
            </div>
            <div>
              <div className="field-lbl">Tên hiển thị</div>
              <input
                className="field-inp"
                value={nu.displayName}
                onChange={(e) => setNu({ ...nu, displayName: e.target.value })}
                style={{ marginBottom: 0 }}
              />
            </div>
            <div>
              <div className="field-lbl">Mật khẩu</div>
              <input
                className="field-inp"
                type="password"
                value={nu.password}
                onChange={(e) => setNu({ ...nu, password: e.target.value })}
                style={{ marginBottom: 0 }}
              />
            </div>
            <div>
              <div className="field-lbl">Quyền</div>
              <select
                style={selectStyle}
                value={nu.role}
                onChange={(e) => setNu({ ...nu, role: Number(e.target.value) })}
              >
                {ROLES.map((r) => (
                  <option key={r.v} value={r.v}>
                    {ROLE_NAME[r.v]}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="mbtn mbtn-primary"
              onClick={add}
              disabled={busy}
              style={{ height: "42px", width: "100%" }}
            >
              ＋ Thêm
            </button>
          </div>
        </div>
      </div>

      {/* Users List Panel */}
      <div>
        <div style={{ fontSize: "13px", fontWeight: 700, color: "var(--text)", marginBottom: "10px" }}>
          Danh sách tài khoản {!loading && `· ${users.length}`}
        </div>
        
        <div style={{
          border: "1px solid var(--border)",
          borderRadius: "12px",
          overflow: "hidden",
          background: "var(--surface)",
        }}>
          <div style={{ overflowX: "auto", maxHeight: "360px" }} className="scroll">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Tài khoản</th>
                  <th style={thStyle}>Quyền</th>
                  <th style={thStyle}>Trạng thái</th>
                  <th style={thStyle}>Đăng nhập gần nhất</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} style={{
                      textAlign: "center",
                      padding: "24px",
                      color: "var(--faint)",
                      fontFamily: "var(--font-mono)",
                      fontSize: "13px",
                    }}>
                      Đang tải danh sách tài khoản...
                    </td>
                  </tr>
                ) : (
                  users.map((u) => {
                    const isMe = me && u.UserId === me.uid;
                    return (
                      <tr key={u.UserId} className="table-row-hover">
                        <td style={tdStyle}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                            <span style={{ fontWeight: 600, color: "var(--text)", fontSize: "13.5px" }}>
                              {u.DisplayName || u.UserName}
                            </span>
                            <span style={{ color: "var(--faint)", fontFamily: "var(--font-mono)", fontSize: "11px" }}>
                              @{u.UserName} {isMe && <span style={{ color: "var(--primary)", fontWeight: "bold" }}>(bạn)</span>}
                            </span>
                          </div>
                        </td>
                        <td style={tdStyle}>
                          <select
                            style={{
                              ...selectStyle,
                              height: "32px",
                              padding: "0 8px",
                              fontSize: "12px",
                              width: "110px",
                              marginBottom: 0,
                            }}
                            value={u.Role || 3}
                            disabled={isMe}
                            onChange={(e) => changeRole(u, e.target.value)}
                          >
                            {ROLES.map((r) => (
                              <option key={r.v} value={r.v}>
                                {ROLE_NAME[r.v]}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={tdStyle}>
                          <span style={{
                            fontFamily: "var(--font-mono)",
                            fontSize: "12px",
                            fontWeight: 700,
                            color: u.IsLocked ? "var(--red)" : "var(--green)",
                          }}>
                            {u.IsLocked ? "🔒 Khoá" : "● Hoạt động"}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, color: "var(--dim)", fontFamily: "var(--font-mono)", fontSize: "12px" }}>
                          {u.LastLoginDate ? fmtVnTime(u.LastLoginDate) : "—"}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                          <span style={{ display: "inline-flex", gap: "5px" }}>
                            <button
                              className="act-btn"
                              title="Đổi mật khẩu"
                              onClick={() => resetPw(u)}
                            >
                              ⚿
                            </button>
                            {!isMe && (
                              <>
                                <button
                                  className="act-btn"
                                  title={u.IsLocked ? "Mở khoá" : "Khoá"}
                                  onClick={() => toggleLock(u)}
                                >
                                  {u.IsLocked ? "🔓" : "🔒"}
                                </button>
                                <button
                                  className="act-btn-danger"
                                  title="Xoá"
                                  onClick={() => remove(u)}
                                >
                                  ✕
                                </button>
                              </>
                            )}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      <style>{`
        .table-row-hover:hover {
          background: var(--hover) !important;
        }
        .act-btn {
          width: 28px;
          height: 28px;
          border-radius: 7px;
          background: transparent;
          border: 1px solid var(--border2);
          color: var(--dim);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          transition: all 0.2s ease;
        }
        .act-btn:hover {
          color: var(--primary) !important;
          border-color: var(--primary) !important;
        }
        .act-btn-danger {
          width: 28px;
          height: 28px;
          border-radius: 7px;
          background: transparent;
          border: 1px solid var(--border2);
          color: var(--dim);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          transition: all 0.2s ease;
        }
        .act-btn-danger:hover {
          color: var(--red) !important;
          border-color: var(--red) !important;
        }
      `}</style>
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  fontSize: "10.5px",
  fontWeight: "700",
  letterSpacing: ".8px",
  textTransform: "uppercase",
  color: "var(--faint)",
  padding: "10px 18px",
  background: "var(--surface2)",
  borderBottom: "1px solid var(--border)",
};

const tdStyle = {
  padding: "12px 18px",
  borderBottom: "1px solid var(--border)",
  fontSize: "13px",
  verticalAlign: "middle",
};

const selectStyle = {
  width: "100%",
  height: "42px",
  padding: "0 13px",
  borderRadius: "10px",
  background: "var(--surface2)",
  border: "1px solid var(--border)",
  color: "var(--text)",
  fontSize: "14px",
  outline: "none",
  boxSizing: "border-box",
  cursor: "pointer",
  marginBottom: 0,
};
